import { useState, useCallback } from 'react';
import { startOfDay } from 'date-fns';
import s from './history.module.scss'
import { IconUsers } from '@tabler/icons-react';
import { useMedia } from 'use-media';
import { PageTemplate } from '@shared/ui/page-template';
import { Button } from '@shared/ui/button/button';
import { SupportButton } from '@shared/ui/support-button';
import { Dropdown } from '@shared/ui/dropdown/dropdown';
import { HistoryBanner } from '@shared/ui/history-banner/history-banner';
import { AddTeacherReviewModal } from '@features/student/add-teacher-review';
import { useAddTeacherReviewModal } from '@features/student/add-teacher-review/model/useAddTeacherReviewModal';
import { useRemoveAppointmentModal } from '@features/appointment/remove-appointment/model/use-remove-appointment-modal';
import { useAppointmentModal } from '@features/appointment/appointment/model/useAppointmentModal';
import { useAppointmentsHistory } from '@widgets/history/api/useAppointmentsHistory';
import { useAllAppointments } from '@entities/schedule/api/useAllAppointments';
import { useActivePacks } from '@widgets/history/api/useActivePacks';
import { useArchivedPacks } from '@widgets/history/api/useArchivedPacks';
import { LessonsList } from '@widgets/history/ui/lessons-list';
import { PacksList } from '@widgets/history/ui/packs-list';
import { useHistoryData, FILTER_OPTIONS } from '@widgets/history/lib/useHistoryData';
import { mapStudentPack, mapStudentLesson } from '@widgets/history/lib/mappers';

const TODAY_START = startOfDay(new Date()).toISOString()

export const History = () => {
    const [switchActive, setSwitchActive] = useState(false)
    const [filter, setFilter] = useState(null)

    const { data: activePacks } = useActivePacks()
    const { data: archivePacks } = useArchivedPacks()
    const { data: history, fetchNextPage, hasNextPage, isFetchingNextPage } = useAppointmentsHistory()
    const { data: upcoming } = useAllAppointments({ from: TODAY_START })

    const isMobile = useMedia({ maxWidth: '768px' })

    const { open: openAddTeacherReview } = useAddTeacherReviewModal()
    const { open: openRemoveAppointmentModal } = useRemoveAppointmentModal()
    const { open: openAppointmentModal } = useAppointmentModal()

    const handleOpenLesson = useCallback((appointmentId) => {
        openAppointmentModal({ appointmentId })
    }, [openAppointmentModal])

    const handleLeaveReview = useCallback((teacherId) => {
        openAddTeacherReview({ teacher_id: teacherId })
    }, [openAddTeacherReview])

    const handleOpenCancelModal = useCallback((appointmentId) => {
        openRemoveAppointmentModal({ appointmentId })
    }, [openRemoveAppointmentModal])

    const { packsToShow, lessonsToShow, activeCount, completedCount } = useHistoryData({
        switchActive,
        filter,
        activePacks,
        archivePacks,
        history,
        upcoming,
    })

    return (
        <PageTemplate>
            <AddTeacherReviewModal />
            <HistoryBanner isMobile={isMobile} />
            <div className={s.container}>
                <div className={s.header}>
                    <h5 className={s.headText}>Мои занятия</h5>
                    <div className={s.countLessons}>
                        {activeCount} активных • {completedCount} завершённых
                    </div>
                </div>
                <div className={s.buttonGroup}>
                    <div className={s.leftGroup}>
                        <Button
                            label='Одиночные'
                            className={switchActive ? s.switchButton : s.activeButton}
                            onClick={() => setSwitchActive(false)}
                        />
                        <Button
                            label='Пакеты'
                            className={switchActive ? s.activeButton : s.switchButton}
                            onClick={() => setSwitchActive(true)}
                        />
                    </div>
                    <Dropdown
                        label='Все'
                        options={FILTER_OPTIONS}
                        value={filter}
                        onChange={setFilter}
                    />
                </div>
                {switchActive ? (
                    <PacksList
                        key={`packs-${filter ?? 'all'}`}
                        packs={packsToShow}
                        role='student'
                        mapPack={mapStudentPack}
                        onLeaveReview={handleLeaveReview}
                    />
                ) : (
                    <LessonsList
                        key={`lessons-${filter ?? 'all'}`}
                        lessons={lessonsToShow}
                        role='student'
                        mapLesson={mapStudentLesson}
                        onOpenCancelModal={handleOpenCancelModal}
                        onLeaveReview={handleLeaveReview}
                        onOpenLesson={handleOpenLesson}
                    />
                )}
                {!switchActive && hasNextPage && (
                    <div className={s.showMore}>
                        <Button
                            theme='secondary'
                            label={isFetchingNextPage ? 'Загрузка...' : 'Показать ещё'}
                            disabled={isFetchingNextPage}
                            onClick={() => fetchNextPage()}
                        />
                    </div>
                )}
                <div className={s.footer}>
                    <div className={s.leftBlock}>
                        <div className={s.circle}>
                            <IconUsers className={s.icon} size={24} />
                        </div>
                        <div className={s.text}>
                            Нужна помощь? <br />
                            <span className={s.help}>Мы всегда на связи и готовы помочь.</span>
                        </div>
                    </div>
                    <SupportButton
                        className={s.supportWrapper}
                        buttonClassName={s.buttonHelp}
                        label='Написать в поддержку'
                    />
                </div>
            </div>
        </PageTemplate>
    )
}
